import Conf from 'conf';

// Configuration for lazy initialization
let customCwd: string | undefined = process.env.TODESKTOP_CONFIG_DIR;
let ephemeralMode = false;
const memoryStore = new Map<string, string>();
let configInstance: Conf<Record<string, unknown>> | null = null;

/**
 * Set a custom directory for the config store.
 * Must be called before any config operations.
 */
export const setConfigDir = (cwd: string) => {
  customCwd = cwd;
};

/**
 * Enable ephemeral mode - config will only be stored in memory
 * and not persisted to disk.
 */
export const setEphemeralMode = (enabled: boolean) => {
  ephemeralMode = enabled;
};

const getConfigInstance = () => {
  if (!configInstance) {
    configInstance = new Conf({
      configName: 'todesktop-cli',
      ...(customCwd && { cwd: customCwd }),
    });
  }
  return configInstance;
};

export const accessTokenConfigKey = 'accessToken';
export const emailConfigKey = 'email';
export const jwtTokenConfigKey = 'jwtToken';

export const setConfig = (key: string, value: string) => {
  if (ephemeralMode) {
    memoryStore.set(key, value);
  } else {
    getConfigInstance().set(key, value);
  }
};

export const getConfig = (key: string) => {
  if (ephemeralMode) {
    return memoryStore.get(key);
  }
  return getConfigInstance().get(key) as string;
};

export const setAuthConfig = (
  email: string,
  accessToken: string,
  jwtToken: string,
) => {
  setConfig(emailConfigKey, email);
  setConfig(accessTokenConfigKey, accessToken);
  setConfig(jwtTokenConfigKey, jwtToken);
};

export const getAuthConfig = () => {
  const accessToken = getConfig(accessTokenConfigKey);
  const jwtToken = getConfig(jwtTokenConfigKey);
  const email = getConfig(emailConfigKey);

  return { accessToken, jwtToken, email };
};

export const deleteAuthConfig = () => {
  if (ephemeralMode) {
    memoryStore.delete(emailConfigKey);
    memoryStore.delete(accessTokenConfigKey);
    memoryStore.delete(jwtTokenConfigKey);
  } else {
    const config = getConfigInstance();
    config.delete(emailConfigKey);
    config.delete(accessTokenConfigKey);
    config.delete(jwtTokenConfigKey);
  }
};

/**
 * Reset the config store state. Only for testing purposes.
 * @internal
 */
export const resetConfigStoreForTesting = () => {
  customCwd = undefined;
  ephemeralMode = false;
  memoryStore.clear();
  configInstance = null;
};
